import { useMemo } from 'react';
import { OnScreenKeyboard } from '../../components/OnScreenKeyboard';
import { Tile } from '../../components/Tile';
import { VisuallyHidden } from '../../components/VisuallyHidden';
import type { BoardProps, InputKey } from '../../engine/types';
import { cx } from '../../utils/classnames';
import { aggregateLetterStates, evaluateGuess, type CipherMove, type CipherState } from './logic';

export function CipherBoard({ state, status, dispatch, feedback }: BoardProps<CipherState, CipherMove>) {
  const playing = status === 'playing';
  const letterStates = useMemo(
    () => aggregateLetterStates(state.guesses, state.answer),
    [state.guesses, state.answer],
  );

  const onKey = (key: InputKey) => {
    if (key.type === 'char') dispatch({ type: 'add', ch: key.value });
    else if (key.type === 'backspace') dispatch({ type: 'remove' });
    else dispatch({ type: 'submit' });
  };

  const rows = Array.from({ length: state.maxGuesses }, (_, r) => r);
  const lastError = feedback?.tone === 'error' ? feedback.nonce : 0;

  // Screen-reader summary of the most recent guess.
  const lastAnnounce = useMemo(() => {
    const g = state.guesses[state.guesses.length - 1];
    if (!g) return '';
    const ev = evaluateGuess(g, state.answer);
    return `Guess ${state.guesses.length}: ${g
      .split('')
      .map((ch, i) => `${ch.toUpperCase()} ${ev[i]}`)
      .join(', ')}`;
  }, [state.guesses, state.answer]);

  return (
    <div className="cv-board">
      <div className="cv-cipher" role="grid" aria-label="Guess grid">
        {rows.map((r) => {
          const committed = r < state.guesses.length;
          const isCurrent = r === state.guesses.length && playing;
          const guess = committed ? state.guesses[r] : isCurrent ? state.current : '';
          const clues = committed ? evaluateGuess(state.guesses[r], state.answer) : null;
          const isLastCommitted = committed && r === state.guesses.length - 1;

          return (
            <div
              key={isCurrent ? `cur-${lastError}` : `row-${r}`}
              className={cx('cv-cipher__row', isCurrent && lastError && 'cv-anim-shake')}
              role="row"
            >
              {Array.from({ length: state.length }, (_, c) => (
                <Tile
                  key={c}
                  letter={guess[c]}
                  state={clues ? clues[c] : undefined}
                  filled={isCurrent && c < state.current.length}
                  reveal={isLastCommitted}
                  index={c}
                />
              ))}
            </div>
          );
        })}
      </div>

      {state.revealed.length > 0 && (
        <div className="cv-hintbar" aria-hidden="true">
          {[...state.revealed]
            .sort((a, b) => a - b)
            .map((pos) => (
              <span key={pos} className="cv-chip cv-chip--seed">
                #{pos + 1}: {state.answer[pos].toUpperCase()}
              </span>
            ))}
        </div>
      )}

      <div className="cv-board__status" data-tone={feedback?.tone === 'error' ? 'error' : undefined} aria-hidden="true">
        {feedback?.message ?? ''}
      </div>
      <VisuallyHidden>
        <p aria-live="polite">{feedback?.message}</p>
        <p aria-live="polite">{lastAnnounce}</p>
      </VisuallyHidden>

      <OnScreenKeyboard onKey={onKey} letterStates={letterStates} disabled={!playing} />
    </div>
  );
}
